import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Toaster } from 'react-hot-toast';
import Header from '@components/Header';
import Footer from '@components/Footer';
import Container from '@components/Container';
import ProtectedRoute from '@components/ProtectedRoute';
import AdminRoute from '@components/AdminRoute';
import VisitTracker from '@components/VisitTracker';
import Home from '@pages/Home';
import About from '@pages/About';
import Profile from '@pages/Profile';
import Dashboard from '@pages/Dashboard';
import Breathing from '@pages/Breathing';
import BuddhaGame from '@pages/BuddhaGame';
import Privacy from '@pages/Privacy';
import Terms from '@pages/Terms';
import SignIn from '@pages/SignIn';
import SignUp from '@pages/SignUp';
import Settings from '@pages/Settings';
import SessionTemplates from '@pages/SessionTemplates';
import VisionBoards from '@pages/VisionBoards';
import AdminBreathingRatio from '@pages/AdminBreathingRatio';
import AdminBreathingPresets from '@pages/AdminBreathingPresets';
import AdminBackgroundVideos from '@pages/AdminBackgroundVideos';
import AdminMusic from '@pages/AdminMusic';
import AdminVisionBoardBackgrounds from '@pages/AdminVisionBoardBackgrounds';
import AdminVisionBoardTemplates from '@pages/AdminVisionBoardTemplates';
import AdminFeedback from '@pages/AdminFeedback';
import AdminQuote from '@pages/AdminQuote';
import AdminAffirmations from '@pages/AdminAffirmations';
import ResetPassword from '@pages/ResetPassword';
import AllSessions from '@pages/AllSessions';
import Leaders from '@pages/Leaders';
import Stats from '@pages/Stats';
import AdminVisits from '@pages/AdminVisits';
import AdminUsers from '@pages/AdminUsers';
import AdminBadges from '@pages/AdminBadges';
import AdminFeatures from '@pages/AdminFeatures';
import AdminBlog from '@pages/AdminBlog';
import AdminBlogAddPost from '@pages/AdminBlogAddPost';
import AdminBlogEditPost from '@pages/AdminBlogEditPost';
import BlogHome from '@pages/BlogHome';
import BlogPost from '@pages/BlogPost';
import BadgesPublic from '@pages/BadgesPublic';
import VisionBoard from '@pages/VisionBoard';
import DownloadVisionBoard from '@pages/DownloadVisionBoard';
import FeedbackWidget from '@components/FeedbackWidget';

function App() {
  const location = useLocation();
  const hideHeaderFooter =
    location.pathname.startsWith('/breathing') ||
    ['/buddha-game', '/signin', '/signup', '/reset-password'].includes(
      location.pathname
    );

  const [headerHeight, setHeaderHeight] = useState<number>(0);

  useEffect(() => {
    if (hideHeaderFooter) {
      setHeaderHeight(0);
      return;
    }

    const headerEl = document.getElementById('site-header');
    if (!headerEl) {
      setHeaderHeight(0);
      return;
    }

    const update = () =>
      setHeaderHeight(headerEl.getBoundingClientRect().height);

    update();

    let ro: ResizeObserver | null = null;
    if ((window as any).ResizeObserver) {
      ro = new ResizeObserver(() => update());
      ro.observe(headerEl);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, [hideHeaderFooter]);

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden">
      <VisitTracker />
      <Toaster />
      {!hideHeaderFooter && <Header />}
      <main
        className="flex-grow"
        style={{ paddingTop: hideHeaderFooter ? 0 : headerHeight }}
      >
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route path="/breathing" element={<Breathing />} />
          <Route path="/breathing/:presetSlug" element={<Breathing />} />
          <Route path="/buddha-game" element={<BuddhaGame />} />
          <Route
            path="/"
            element={
              <Container>
                <Home />
              </Container>
            }
          />
          <Route
            path="/about"
            element={
              <Container>
                <About />
              </Container>
            }
          />
          <Route
            path="/badges"
            element={
              <Container>
                <BadgesPublic />
              </Container>
            }
          />
          <Route
            path="/vision-board"
            element={
              <Container>
                <VisionBoard />
              </Container>
            }
          />
          <Route
            path="/download-vision-board"
            element={
              <Container>
                <DownloadVisionBoard />
              </Container>
            }
          />
          <Route
            path="/vision-board/edit/:visionBoardId"
            element={<Navigate to="/vision-boards" replace />}
          />
          <Route
            path="/privacy"
            element={
              <Container>
                <Privacy />
              </Container>
            }
          />
          <Route
            path="/terms"
            element={
              <Container>
                <Terms />
              </Container>
            }
          />
          <Route
            path="/profile"
            element={
              <Container>
                <Profile />
              </Container>
            }
          />
          <Route
            path="/profile/:profileName"
            element={
              <Container>
                <Profile />
              </Container>
            }
          />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/blog" element={<BlogHome />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/leaders" element={<Leaders />} />
          <Route
            path="/sessions"
            element={
              <ProtectedRoute>
                <AllSessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <SessionTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vision-boards"
            element={
              <ProtectedRoute>
                <VisionBoards />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stats"
            element={
              <ProtectedRoute>
                <Stats />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/breathing-ratio"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminBreathingRatio />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/breathing-presets"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminBreathingPresets />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/badges"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminBadges />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminBlog />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog/new"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminBlogAddPost />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog/:postId/edit"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminBlogEditPost />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/features"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminFeatures />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/background-videos"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminBackgroundVideos />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/music"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminMusic />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vision-board-backgrounds"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminVisionBoardBackgrounds />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vision-board-templates"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminVisionBoardTemplates />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quotes"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminQuote />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/affirmations"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminAffirmations />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/visits"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminVisits />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/feedback"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminFeedback />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          {/* Fallback route to avoid development overlay warnings for unmatched paths */}
          <Route
            path="*"
            element={
              <Container>
                <Home />
              </Container>
            }
          />
        </Routes>
      </main>
      {!hideHeaderFooter && <Footer />}
      <Analytics />
      <FeedbackWidget />
    </div>
  );
}

export default App;
